/**
 * src/components/primitives/ConfirmModal.tsx — typed-confirm dialog (FR-008).
 *
 * A typed-confirm modal that ARMS its primary action only when the
 * operator's input matches the `typed` prop EXACTLY. Empty, partial,
 * whitespace-padded, or case-mismatched input keeps the confirm action
 * disabled — a typed-confirm modal that fires on any click is not a
 * confirm at all (CLAUDE.md §5 honest-state invariant).
 *
 * Exact-match semantics:
 *   - `val === typed`  → ARM (no normalization, no trim, no lowercase).
 *   - anything else    → DISABLE.
 *   - `typed` undefined → ARM (no typed-confirm requirement; the modal
 *     becomes a one-click destructive confirm — used for low-stakes
 *     surfaces where the typed gesture is overkill).
 *
 * Close + reset semantics:
 *   - Closed (`open === false`) → render `null`. NEVER leak title or
 *     body text into the DOM (the operator cannot see a destructive
 *     affordance that is not active).
 *   - Re-opening (`open` flips `false → true`) clears any previously
 *     typed value — the next operator must type the confirmation again.
 *
 * Native-disabled invariant: the confirm button uses the native
 * `<button disabled>` attribute so jsdom/the browser block the click
 * before the wrapped handler fires. A refactor that drops `disabled`
 * and only hides the click visually fails the suite.
 *
 * Accessibility: the modal carries `role="dialog"` and `aria-modal="true"`;
 * `Escape` closes the dialog; a backdrop click closes the dialog.
 *
 * RED-on-regression: any case where a partial typed value arms the
 * confirm button, or where the modal renders title/body while closed,
 * fails `tests/primitives/access-gate.test.ts` (the ConfirmModal suite,
 * T309 → T310).
 */
import React, {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export interface ConfirmModalProps {
  /** Render gate. Closed → renders `null` (no leaked dialog content). */
  open: boolean;
  /** Fired on backdrop click, `Escape`, the Cancel button, or after confirm. */
  onClose: () => void;
  /** Dialog title text. */
  title: string;
  /** Dialog body. ReactNode so callers can pass formatted copy. */
  body: ReactNode;
  /** Primary action label. Defaults to `'Confirmar'` (the prototype's Spanish copy). */
  confirmLabel?: string;
  /** When true, the primary action renders in the destructive (red) palette. */
  danger?: boolean;
  /**
   * Required typed-confirmation string. The operator must type this
   * EXACTLY into the input before the primary action arms. If omitted,
   * the action arms immediately (no typed-confirm gate).
   */
  typed?: string;
  /** Fired when the operator clicks the (armed) primary action. */
  onConfirm: () => void;
}

const BACKDROP_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  backdropFilter: 'blur(4px)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const PANEL_STYLE: CSSProperties = {
  background: 'var(--canvas)',
  borderRadius: 22,
  width: '100%',
  maxWidth: 460,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 24px 64px rgba(0,0,0,0.24)',
};

const HEADER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  borderBottom: '1px solid var(--lightest-gray)',
};

const FOOTER_STYLE: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  padding: '16px 24px',
  borderTop: '1px solid var(--lightest-gray)',
};

const CONFIRM_BASE_STYLE: CSSProperties = {
  padding: '9px 18px',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'var(--font-sans)',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid transparent',
  cursor: 'pointer',
  lineHeight: 1,
};

const CANCEL_STYLE: CSSProperties = {
  ...CONFIRM_BASE_STYLE,
  background: 'transparent',
  color: 'var(--ink-muted)',
  borderColor: 'var(--border-cool)',
  cursor: 'pointer',
};

export function ConfirmModal({
  open,
  onClose,
  title,
  body,
  confirmLabel = 'Confirmar',
  danger,
  typed,
  onConfirm,
}: ConfirmModalProps): React.ReactElement | null {
  const [val, setVal] = useState('');

  // Reset the typed value every time the modal opens. A previously typed
  // confirmation must not survive a close → re-open cycle.
  useEffect(() => {
    if (open) setVal('');
  }, [open]);

  // Escape closes the dialog while open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Closed → nothing in the DOM. NEVER render the title or body.
  if (!open) return null;

  // ARM only on EXACT equality (no normalization). If `typed` is omitted,
  // arm immediately — the typed-confirm gate is opt-in per call site.
  const armed = typed === undefined || val === typed;

  const confirmStyle: CSSProperties = {
    ...CONFIRM_BASE_STYLE,
    background: danger ? 'transparent' : 'var(--ink-muted)',
    color: danger ? 'var(--bad)' : '#fff',
    borderColor: danger ? 'var(--bad)' : 'var(--ink-muted)',
    opacity: armed ? 1 : 0.5,
    cursor: armed ? 'pointer' : 'not-allowed',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="circa-confirm-title"
      style={BACKDROP_STYLE}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={PANEL_STYLE}>
        <header style={HEADER_STYLE}>
          <h2
            id="circa-confirm-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontWeight: 400,
              letterSpacing: '-0.3px',
            }}
          >
            {title}
          </h2>
        </header>
        <div style={{ padding: 24, overflow: 'auto' }}>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              color: 'var(--body-muted)',
              lineHeight: 1.55,
            }}
          >
            {body}
          </p>
          {typed !== undefined && (
            <div style={{ marginTop: 18 }}>
              <label style={{ display: 'block' }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--ink-muted)',
                    marginBottom: 7,
                  }}
                >
                  Escribe{' '}
                  <b style={{ fontFamily: 'var(--font-mono)' }}>{typed}</b>{' '}
                  para confirmar
                </span>
                <input
                  type="text"
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder={typed}
                  style={{
                    width: '100%',
                    border: '1px solid var(--border-cool)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 14,
                    fontFamily: 'var(--font-sans)',
                    color: 'var(--ink-muted)',
                    outline: 'none',
                  }}
                />
              </label>
            </div>
          )}
        </div>
        <footer style={FOOTER_STYLE}>
          <button type="button" onClick={onClose} style={CANCEL_STYLE}>
            Cancelar
          </button>
          <button
            type="button"
            disabled={!armed}
            onClick={() => {
              if (!armed) return;
              onConfirm();
              onClose();
            }}
            style={confirmStyle}
          >
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default ConfirmModal;
